import { DomainError } from './errors';
import type {
  DeclareTemperatureIncidentInput,
  FoodContainer,
  IncidentResolution,
  InventoryEvent,
  InventorySnapshot,
  RecordTemperatureReadingInput,
  StorageNode,
  TemperatureIncident,
  TemperatureReading,
  TemperatureTimeBoundary,
} from './model';

const ACTIVE_STATUSES = new Set<FoodContainer['status']>(['present', 'partial']);
const MIN_TEMPERATURE_C = -80;
const MAX_TEMPERATURE_C = 50;

const validateTemperature = (value: number | null, label: string): void => {
  if (
    value != null &&
    (!Number.isFinite(value) || value < MIN_TEMPERATURE_C || value > MAX_TEMPERATURE_C)
  ) {
    throw new DomainError(
      `${label} doit être comprise entre ${MIN_TEMPERATURE_C} et ${MAX_TEMPERATURE_C} °C.`,
      'INVALID_STATE',
    );
  }
};

const validateBoundary = (boundary: TemperatureTimeBoundary, label: string): void => {
  if (boundary.certainty === 'unknown' && boundary.value != null) {
    throw new DomainError(`${label} inconnue ne doit pas contenir d’heure.`, 'INVALID_STATE');
  }
  if (boundary.certainty !== 'unknown' && boundary.value == null) {
    throw new DomainError(`${label} doit être renseignée ou déclarée inconnue.`, 'INVALID_STATE');
  }
  if (boundary.value != null && Number.isNaN(Date.parse(boundary.value))) {
    throw new DomainError(`${label} est invalide.`, 'INVALID_STATE');
  }
};

export const createManualTemperatureReading = (
  id: string,
  householdId: string,
  input: RecordTemperatureReadingInput,
  recordedAt: string,
  idempotencyKey: string,
): TemperatureReading => {
  validateTemperature(input.currentTemperatureC, 'La température actuelle');
  validateTemperature(input.minimumTemperatureC, 'La température minimale');
  validateTemperature(input.maximumTemperatureC, 'La température maximale');
  if (input.resetCertainty === 'unknown' && input.resetOn != null) {
    throw new DomainError(
      'Une remise à zéro inconnue ne doit pas contenir de date.',
      'INVALID_STATE',
    );
  }
  if (input.resetCertainty !== 'unknown' && input.resetOn == null) {
    throw new DomainError(
      'Indiquez la date de remise à zéro ou choisissez « inconnue ».',
      'INVALID_STATE',
    );
  }
  if (
    input.minimumTemperatureC != null &&
    input.minimumTemperatureC > input.maximumTemperatureC
  ) {
    throw new DomainError(
      'La température minimale ne peut pas dépasser la température maximale.',
      'INVALID_STATE',
    );
  }
  if (
    input.currentTemperatureC != null &&
    (input.minimumTemperatureC != null &&
      input.currentTemperatureC < input.minimumTemperatureC)
  ) {
    throw new DomainError(
      'La température actuelle ne peut pas être inférieure au minimum relevé.',
      'INVALID_STATE',
    );
  }
  if (
    input.currentTemperatureC != null &&
    input.currentTemperatureC > input.maximumTemperatureC
  ) {
    throw new DomainError(
      'La température actuelle ne peut pas dépasser le maximum relevé.',
      'INVALID_STATE',
    );
  }
  return {
    id,
    householdId,
    freezerId: input.freezerId,
    deviceLabel: input.deviceLabel.trim() || 'Thermomètre mécanique mini/maxi',
    recordedAt,
    currentTemperatureC: input.currentTemperatureC,
    minimumTemperatureC: input.minimumTemperatureC,
    maximumTemperatureC: input.maximumTemperatureC,
    unit: 'C',
    quality: 'manual',
    provenance: 'mechanical_min_max',
    resetOn: input.resetOn,
    resetCertainty: input.resetCertainty,
    note: input.note.trim(),
    idempotencyKey,
  };
};

interface ContainerStatePoint {
  at: number;
  state: FoodContainer;
}

const freezerFor = (
  locationId: string,
  locationsById: ReadonlyMap<string, StorageNode>,
): string | null => locationsById.get(locationId)?.freezerId ?? null;

const statePointsFor = (
  container: FoodContainer,
  events: InventoryEvent[],
): ContainerStatePoint[] => {
  const points = events
    .filter((event) => event.containerId === container.id && event.payload.after != null)
    .map((event) => ({
      at: Date.parse(event.occurredAt),
      state: event.payload.after as FoodContainer,
    }))
    .filter((point) => Number.isFinite(point.at))
    .sort((left, right) => left.at - right.at);
  if (points.length === 0) {
    points.push({ at: Date.parse(container.createdAt), state: container });
  }
  return points;
};

export const findExposedContainerIds = (
  freezerId: string,
  started: TemperatureTimeBoundary,
  ended: TemperatureTimeBoundary,
  declaredAt: string,
  containers: FoodContainer[],
  events: InventoryEvent[],
  locations: StorageNode[],
): string[] => {
  validateBoundary(started, 'La date de début');
  validateBoundary(ended, 'La date de fin');
  const declaredAtMs = Date.parse(declaredAt);
  const startMs = started.value == null ? Number.NEGATIVE_INFINITY : Date.parse(started.value);
  const requestedEndMs = ended.value == null ? declaredAtMs : Date.parse(ended.value);
  if (startMs > declaredAtMs || requestedEndMs > declaredAtMs) {
    throw new DomainError(
      'La période d’un incident déclaré ne peut pas être située dans le futur.',
      'INVALID_STATE',
    );
  }
  const endMs = requestedEndMs;
  if (startMs > endMs) {
    throw new DomainError('Le début de l’incident doit précéder sa fin.', 'INVALID_STATE');
  }
  const locationsById = new Map(locations.map((location) => [location.id, location]));
  return containers
    .filter((container) => {
      const points = statePointsFor(container, events);
      return points.some((point, index) => {
        const intervalStart = point.at;
        const intervalEnd = Math.min(points[index + 1]?.at ?? declaredAtMs, declaredAtMs);
        return (
          ACTIVE_STATUSES.has(point.state.status) &&
          freezerFor(point.state.locationId, locationsById) === freezerId &&
          intervalStart <= endMs &&
          intervalEnd >= startMs
        );
      });
    })
    .map((container) => container.id)
    .sort();
};

export const createTemperatureIncident = (
  id: string,
  householdId: string,
  input: DeclareTemperatureIncidentInput,
  affectedContainerIds: string[],
  createdAt: string,
  idempotencyKey: string,
): TemperatureIncident => {
  validateBoundary(input.started, 'La date de début');
  validateBoundary(input.ended, 'La date de fin');
  validateTemperature(input.maximumTemperatureC, 'La température maximale');
  if (
    input.started.value != null &&
    input.ended.value != null &&
    input.started.value > input.ended.value
  ) {
    throw new DomainError('Le début de l’incident doit précéder sa fin.', 'INVALID_STATE');
  }
  return {
    id,
    idempotencyKey,
    householdId,
    freezerId: input.freezerId,
    kind: input.kind,
    source: input.source,
    started: structuredClone(input.started),
    ended: structuredClone(input.ended),
    maximumTemperatureC: input.maximumTemperatureC,
    note: input.note.trim(),
    affectedContainerIds: [...new Set(affectedContainerIds)].sort(),
    status: 'open',
    createdAt,
    updatedAt: createdAt,
  };
};

export const latestResolutionsByContainer = (
  incidentId: string,
  resolutions: IncidentResolution[],
): Map<string, IncidentResolution> => {
  const latest = new Map<string, IncidentResolution>();
  for (const resolution of resolutions) {
    if (resolution.incidentId !== incidentId) continue;
    const current = latest.get(resolution.containerId);
    if (
      current == null ||
      resolution.decidedAt > current.decidedAt ||
      (resolution.decidedAt === current.decidedAt && resolution.id > current.id)
    ) {
      latest.set(resolution.containerId, resolution);
    }
  }
  return latest;
};

export const deriveIncidentStatus = (
  incident: TemperatureIncident,
  resolutions: IncidentResolution[],
): TemperatureIncident['status'] => {
  if (incident.affectedContainerIds.length === 0) return 'resolved';
  const latest = latestResolutionsByContainer(incident.id, resolutions);
  const cleared = incident.affectedContainerIds.filter(
    (containerId) => latest.get(containerId)?.choice === 'cleared',
  ).length;
  if (cleared === 0) return 'open';
  return cleared === incident.affectedContainerIds.length ? 'resolved' : 'partially_resolved';
};

export const applyTemperatureIncidentFlags = (
  containers: FoodContainer[],
  incidents: TemperatureIncident[],
  resolutions: IncidentResolution[],
): FoodContainer[] => {
  const affectedEver = new Set(incidents.flatMap((incident) => incident.affectedContainerIds));
  const active = new Map<string, string[]>();
  for (const incident of incidents) {
    const latest = latestResolutionsByContainer(incident.id, resolutions);
    for (const containerId of incident.affectedContainerIds) {
      if (latest.get(containerId)?.choice !== 'cleared') {
        const notes = active.get(containerId) ?? [];
        notes.push(incident.note || 'Incident de température déclaré sur le congélateur.');
        active.set(containerId, notes);
      }
    }
  }
  return containers.map((container) => {
    const notes = active.get(container.id);
    if (notes != null) {
      return {
        ...container,
        incidentStatus: 'unresolved',
        incidentNote: [...new Set(notes)].join(' · ').slice(0, 500),
      };
    }
    if (affectedEver.has(container.id)) {
      return { ...container, incidentStatus: 'resolved', incidentNote: '' };
    }
    return container;
  });
};

export const previewIncidentExposure = (
  snapshot: InventorySnapshot,
  input: DeclareTemperatureIncidentInput,
  declaredAt = new Date().toISOString(),
): string[] =>
  findExposedContainerIds(
    input.freezerId,
    input.started,
    input.ended,
    declaredAt,
    snapshot.containers,
    snapshot.events,
    snapshot.locations,
  );
